import { useState, useEffect, useCallback, useRef } from 'react';
import { getNowPlaying, getIcecastMetadata } from '../../api/client';
import { HiClock, HiInformationCircle, HiMusicalNote } from 'react-icons/hi2';
import type { LiveStatus } from '../../types';

export interface ShowInfo {
  title: string;
  description?: string;
  image?: string;
}

interface NowPlayingProps {
  streamId: number;
  pollInterval?: number; // ms, default 30s
  onShowInfo?: (info: ShowInfo) => void;
  onImageChange?: (image: string | null) => void;
  showNowPlaying?: boolean;
  showIcecastTrack?: boolean;
}

function formatTime(timestamp: number, timezone?: string): string {
  const date = new Date(timestamp * 1000);
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  if (timezone) {
    try {
      return date.toLocaleTimeString([], { ...opts, timeZone: timezone });
    } catch { /* invalid tz — fall through */ }
  }
  return date.toLocaleTimeString([], opts);
}

function getTimeUntil(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = timestamp - now;

  if (diff <= 0) return 'Now';
  if (diff < 60) return 'In less than a minute';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `In ${mins} min${mins > 1 ? 's' : ''}`;
  }
  const hours = Math.floor(diff / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  if (mins === 0) return `In ${hours}h`;
  return `In ${hours}h ${mins}m`;
}

function TrackDisplay({ track }: { track: string }) {
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <HiMusicalNote size={12} className="text-[var(--color-primary)] flex-shrink-0" />
      <p className="text-xs text-[var(--color-text-secondary)] truncate">{track}</p>
    </div>
  );
}

export function NowPlaying({ streamId, pollInterval = 30000, onShowInfo, onImageChange, showNowPlaying = true, showIcecastTrack = false }: NowPlayingProps) {
  const [schedule, setSchedule] = useState<LiveStatus | null>(null);
  const [loading, setLoading] = useState(showNowPlaying);
  const [currentTrack, setCurrentTrack] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    if (!showNowPlaying) return;
    try {
      const data = await getNowPlaying(streamId);
      setSchedule(data);
    } catch (err) {
      console.error('Failed to fetch now playing:', err);
    } finally {
      setLoading(false);
    }
  }, [streamId, showNowPlaying]);

  const fetchIcecastTrack = useCallback(async () => {
    if (!showIcecastTrack) return;
    try {
      const data = await getIcecastMetadata(streamId);
      setCurrentTrack(data.track);
    } catch {
      // Silently fail - track display is optional
    }
  }, [streamId, showIcecastTrack]);

  // Initial fetch
  useEffect(() => {
    fetchSchedule();
    fetchIcecastTrack();
  }, [fetchSchedule, fetchIcecastTrack]);

  // Polling - schedule every 30s, icecast every 15s
  useEffect(() => {
    if (!showNowPlaying) return;
    const scheduleInterval = setInterval(fetchSchedule, pollInterval);
    return () => clearInterval(scheduleInterval);
  }, [fetchSchedule, pollInterval, showNowPlaying]);

  useEffect(() => {
    if (!showIcecastTrack) return;
    const trackInterval = setInterval(fetchIcecastTrack, 15000);
    return () => clearInterval(trackInterval);
  }, [fetchIcecastTrack, showIcecastTrack]);

  // Re-fetch when the current show ends so now/next transitions immediately
  const endTimeRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const endTime = schedule?.current?.endTime;
    // Avoid re-setting the same timer if endTime hasn't changed
    if (endTime === endTimeRef.current) return;
    endTimeRef.current = endTime;
    if (!endTime) return;

    const msUntilEnd = endTime * 1000 - Date.now() + 1000; // +1s buffer
    if (msUntilEnd <= 0) {
      // Already past — fetch immediately
      fetchSchedule();
      return;
    }
    const timer = setTimeout(fetchSchedule, msUntilEnd);
    return () => clearTimeout(timer);
  }, [schedule?.current?.endTime, fetchSchedule]);

  // Tick every 60s to keep "Up Next · In X mins" countdown text fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!schedule?.next) return;
    const ticker = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(ticker);
  }, [schedule?.next]);

  // Notify parent when the current show's image changes
  useEffect(() => {
    if (!onImageChange) return;
    if (schedule?.current?.image) {
      onImageChange(schedule.current.image);
    } else {
      onImageChange(null);
    }
  }, [schedule?.current?.image, onImageChange]);

  // Track-only mode (no now/next)
  if (!showNowPlaying) {
    if (!currentTrack) return null;
    return (
      <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-[var(--color-surface)]/30" aria-live="polite">
        <HiMusicalNote size={14} className="text-[var(--color-primary)] flex-shrink-0" />
        <p className="text-sm text-[var(--color-text-secondary)] truncate">{currentTrack}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-sm" role="status">
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        <span>Loading schedule...</span>
      </div>
    );
  }

  // No current show - show "Up Next" if available, otherwise just "Live" badge
  if (!schedule?.current) {
    if (schedule?.next) {
      return (
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--color-surface)]/30">
            <HiClock size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[var(--color-text-muted)] uppercase font-medium">
                Up Next · {getTimeUntil(schedule.next.startTime)}
              </p>
              <p className="text-sm text-[var(--color-text-secondary)] truncate">
                {schedule.next.title}
              </p>
            </div>
            {onShowInfo && (schedule.next.description || schedule.next.image) && (
              <button
                onClick={() => onShowInfo({
                  title: schedule.next!.title,
                  description: schedule.next!.description,
                  image: schedule.next!.image,
                })}
                aria-label={`Info about ${schedule.next.title}`}
                className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]
                           transition-colors rounded-full flex-shrink-0
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
              >
                <HiInformationCircle size={18} />
              </button>
            )}
          </div>
          {currentTrack && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-surface)]/30">
              <HiMusicalNote size={14} className="text-[var(--color-primary)] flex-shrink-0" />
              <p className="text-sm text-[var(--color-text-secondary)] truncate">{currentTrack}</p>
            </div>
          )}
        </div>
      );
    }
    // No current, no next - show track if available, otherwise Live badge
    if (currentTrack) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-[var(--color-surface)]/30" aria-live="polite">
          <HiMusicalNote size={14} className="text-[var(--color-primary)] flex-shrink-0" />
          <p className="text-sm text-[var(--color-text-secondary)] truncate">{currentTrack}</p>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-2 mb-4">
        <span className="flex items-center gap-1.5 px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-xs font-bold uppercase">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          Live
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3 mb-4" aria-live="polite" aria-atomic="true">
      {/* Current show */}
      <div className="bg-[var(--color-surface-elevated)]/50 rounded-xl p-3 border border-[var(--color-border)]/30">
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center gap-1.5 px-2 py-0.5 bg-red-500/20 text-red-500 rounded-full text-[10px] font-bold uppercase">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            On Air
          </span>
          <span className="text-[10px] text-[var(--color-text-muted)]">
            {formatTime(schedule.current.startTime, schedule.timezone)} - {formatTime(schedule.current.endTime, schedule.timezone)}
          </span>
          {onShowInfo && (schedule.current.description || schedule.current.image) && (
            <button
              onClick={() => onShowInfo({
                title: schedule.current!.title,
                description: schedule.current!.description,
                image: schedule.current!.image,
              })}
              aria-label={`Info about ${schedule.current.title}`}
              className="ml-auto p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]
                         transition-colors rounded-full flex-shrink-0
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            >
              <HiInformationCircle size={16} />
            </button>
          )}
        </div>
        <h3 className="text-base font-semibold text-[var(--color-text)] leading-tight">
          {schedule.current.title}
        </h3>
        {currentTrack && <TrackDisplay track={currentTrack} />}
      </div>

      {/* Next show */}
      {schedule.next && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--color-surface)]/30">
          <HiClock size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[var(--color-text-muted)] uppercase font-medium">
              Up Next · {getTimeUntil(schedule.next.startTime)}
            </p>
            <p className="text-sm text-[var(--color-text-secondary)] truncate">
              {schedule.next.title}
            </p>
          </div>
          {onShowInfo && (schedule.next.description || schedule.next.image) && (
            <button
              onClick={() => onShowInfo({
                title: schedule.next!.title,
                description: schedule.next!.description,
                image: schedule.next!.image,
              })}
              aria-label={`Info about ${schedule.next.title}`}
              className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]
                         transition-colors rounded-full flex-shrink-0
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            >
              <HiInformationCircle size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
